import { isCharacterCategory, SheetCategory } from "./sheet";
import { SheetSubjectKind, SUBJECT_KINDS } from "./sheet-subject";
import {
  DocumentReference,
  DocumentSnapshot,
  Query,
} from "@atgof-firebase/types";
import { displayNameForId, idForDisplayName } from "./util";
import { Character } from "./generated/entities/project/character";

export type PerformerKind = Character["kind"];

export type SubjectSpec = {
  subjectKind: SheetSubjectKind;
  subjectName: string;
  performerKind?: PerformerKind;
};

export function subjectsAttr(
  subjectKind: SheetSubjectKind,
  performerKind?: Character["kind"],
) {
  return subjectKind === "character"
    ? performerKind === "sa"
      ? "nonCast.sa"
      : "cast"
    : "sets";
}

export function hasSubject(
  q: Query,
  { subjectKind, subjectName, performerKind }: SubjectSpec,
) {
  return q.where(
    subjectsAttr(subjectKind, performerKind),
    "array-contains",
    subjectName,
  );
}

export function subjectKindForCategory(category: SheetCategory | undefined) {
  if (isCharacterCategory(category)) return "character";
  if (category === "arts") return "set";
  return;
}

export function subjectSpecForSheet(
  sheet: DocumentSnapshot | undefined,
): SubjectSpec | undefined {
  const subjectKind = subjectKindForCategory(sheet?.get("category"));
  if (subjectKind) return { subjectKind, subjectName: sheet!.get(subjectKind) };
  return;
}

export const idForSubjectName = idForDisplayName;

export const subjectNameForId = displayNameForId;

export function subjectCollection(
  project: DocumentReference | undefined,
  subjectKind: string | undefined,
) {
  if (
    !(subjectKind && SUBJECT_KINDS.includes(subjectKind as SheetSubjectKind))
  ) {
    return;
  }
  return project?.collection(`${subjectKind}s`);
}

export function subjectRef(
  project: DocumentReference | undefined,
  { subjectKind, subjectName }: SubjectSpec,
) {
  return subjectCollection(project, subjectKind)?.doc(
    idForSubjectName(subjectName),
  );
}

export function kindOfSubject(
  subjectRef: DocumentReference | undefined,
): SheetSubjectKind | undefined {
  const collId = subjectRef?.parent.id;
  return collId === "characters"
    ? "character"
    : collId === "sets"
      ? "set"
      : undefined;
}

export function projectRefForSubject(
  subjectRef: DocumentReference | null | undefined,
) {
  return subjectRef?.parent.parent ?? undefined;
}

export function actualPerformerKind(
  displayId: string | undefined,
  performerKind: PerformerKind | undefined,
): PerformerKind {
  const identifier = displayId?.toLocaleUpperCase();
  if (
    identifier?.endsWith("SC") /* Stunt co-ordinator */ || // TODO Are these industry-standard?
    identifier?.endsWith("ST") /* Stunt (probably should be SD) */ ||
    identifier?.endsWith("SD") /* Stunt double */
  ) {
    return "stunt";
  }
  if (identifier?.endsWith("SA")) return "sa";
  return performerKind ?? "cast";
}
